import { useMergeLink } from "@mergeapi/react-merge-link";
import { useToast } from "@src/hooks/useToast";
import { useTracking } from "@src/hooks/useTracking";
import { gql } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

import { GraphqlOperations } from "@src/types.generated";
import {
  useGenerateAccountTokenMutation,
  useGenerateLinkTokenMutation,
} from "./useOpenMergeLink.generated";

export const useOpenMergeLink = (
  initialIntegrationId?: string,
): (() => void) => {
  const [linkToken, setLinkToken] = useState("");
  const [newIntegrationId, setNewIntegrationId] = useState("");
  const [generateLinkToken] = useGenerateLinkTokenMutation({
    onCompleted: (data) => {
      if (
        data.generateMergeLinkToken.value &&
        data.generateMergeLinkToken.hrisIntegrationId
      ) {
        setLinkToken(data.generateMergeLinkToken.value);
        setNewIntegrationId(data.generateMergeLinkToken.hrisIntegrationId);
      }
    },
  });

  const { addToast } = useToast();
  const { trackEvent } = useTracking();
  const [generateAccountToken] = useGenerateAccountTokenMutation();
  const onSuccess = useCallback(
    (publicToken: string) => {
      if (!newIntegrationId) {
        return;
      }
      trackEvent("connected_hris", {});
      addToast({
        message:
          "Your HRIS has been connected! It can take several minutes to pull in your data.",
      });
      generateAccountToken({
        refetchQueries: [
          GraphqlOperations.Query.OrgHrisConfig,
          GraphqlOperations.Query.HrisIntegrations,
        ],
        variables: {
          publicToken,
          hrisIntegrationId: newIntegrationId,
        },
      });
    },
    [generateAccountToken, trackEvent, addToast, newIntegrationId],
  );

  const onExit = useCallback(() => {
    setLinkToken("");
  }, [setLinkToken]);

  const { open, isReady } = useMergeLink({
    linkToken,
    onSuccess,
    // @ts-ignore
    onExit,
  });
  const canOpenLink = isReady && linkToken;

  useEffect(() => {
    if (canOpenLink) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOpenLink]);

  return useCallback(() => {
    if (canOpenLink) {
      open();
    } else {
      generateLinkToken({
        variables: {
          integrationId: initialIntegrationId,
        },
      });
    }
  }, [canOpenLink, initialIntegrationId, open, generateLinkToken]);
};

gql`
  mutation GenerateAccountToken(
    $publicToken: String!
    $hrisIntegrationId: String!
  ) {
    generateMergeAccountToken(
      publicToken: $publicToken
      hrisIntegrationId: $hrisIntegrationId
    ) {
      success
    }
  }
  mutation GenerateLinkToken($integrationId: UUID) {
    generateMergeLinkToken(integrationId: $integrationId) {
      value
      hrisIntegrationId
    }
  }
`;
